import type { AxiosInstance } from 'axios';

import {
  EMPTY_SCORES,
  totalScore,
  TPresentationScores,
} from '../../../../components/Tournament/PresentationScoring';
import {
  API_ERRORS,
  getApiErrorMessage,
  getApiErrorName,
} from '../../../../services/api';
import {
  IAgendaItem,
  pickInitialTeam,
} from '../../../../utils/tournament/presentationAgenda';

/** Notas como vêm da API (BE-58). */
export interface IScheduleScores extends TPresentationScores {
  total_score: number;
  updated_at?: string;
}

export interface IScheduleTeam {
  team_id: string;
  team_name: string;
  school_name: string | null;
  category: string;
  disqualified: boolean;
  scored: boolean;
  scores: IScheduleScores | null;
}

export interface IScheduledPresentation extends IScheduleTeam {
  slot_id: string;
  starts_at: string;
  ends_at: string;
}

export interface IScheduleRoom {
  id: string;
  name: string;
  presentations: IScheduledPresentation[];
}

export interface IPresentationSchedule {
  rooms: IScheduleRoom[];
  unscheduled: IScheduleTeam[];
}

/** Equipe da aba com sala e horário (ou sem horário). */
export interface IAgendaTeam extends IScheduleTeam {
  slot?: {
    slot_id: string;
    room_id: string;
    room_name: string;
    starts_at: string;
    ends_at: string;
  };
}

export function agendaTeams(schedule: IPresentationSchedule): IAgendaTeam[] {
  return [
    ...schedule.rooms.flatMap(room =>
      room.presentations.map(p => ({
        ...p,
        slot: {
          slot_id: p.slot_id,
          room_id: room.id,
          room_name: room.name,
          starts_at: p.starts_at,
          ends_at: p.ends_at,
        },
      })),
    ),
    ...schedule.unscheduled,
  ];
}

export const toAgendaItems = (teams: IAgendaTeam[]): IAgendaItem[] =>
  teams.map(team => ({
    team_id: team.team_id,
    team_name: team.team_name,
    category: team.category,
    status: team.scored ? 'scored' : 'pending',
    schedule: team.slot ?? null,
  }));

/** Notas salvas da equipe (sem o total), para começar o formulário. */
export const savedScores = (team: IScheduleTeam): TPresentationScores =>
  team.scores
    ? {
        mechanical_score: team.scores.mechanical_score,
        programming_score: team.scores.programming_score,
        teamwork_score: team.scores.teamwork_score,
        communication_score: team.scores.communication_score,
      }
    : { ...EMPTY_SCORES };

/** A agenda depois de a nota de uma equipe ser salva, sem recarregar. */
export function applySaved(
  schedule: IPresentationSchedule,
  teamId: string,
  scores: TPresentationScores,
): IPresentationSchedule {
  const mark = <T extends IScheduleTeam>(team: T): T =>
    team.team_id === teamId
      ? {
          ...team,
          scored: true,
          scores: { ...scores, total_score: totalScore(scores) },
        }
      : team;

  return {
    rooms: schedule.rooms.map(room => ({
      ...room,
      presentations: room.presentations.map(mark),
    })),
    unscheduled: schedule.unscheduled.map(mark),
  };
}

/** Próxima equipe pendente da agenda (a da vez primeiro), sem contar `exceptId`. */
export function nextPendingTeam(
  schedule: IPresentationSchedule,
  nowWall: number,
  exceptId?: string,
): string | null {
  const pending = agendaTeams(schedule).filter(
    team => !team.scored && team.team_id !== exceptId,
  );
  return pickInitialTeam(toAgendaItems(pending), nowWall);
}

export type TSaveResult =
  | { ok: true }
  | { ok: false; kind: 'not-a-judge' | 'failed'; message: string };

/**
 * Salva a nota. Nunca lança: o erro volta como resultado, para a tela mostrar
 * o motivo sem perder o que foi digitado (FE-69).
 */
export async function savePresentation(
  api: Pick<AxiosInstance, 'post'>,
  tournamentId: string,
  teamId: string,
  scores: TPresentationScores,
): Promise<TSaveResult> {
  try {
    await api.post(
      `/tournaments/${tournamentId}/teams/${teamId}/technical-presentation`,
      scores,
    );
    return { ok: true };
  } catch (err) {
    if (getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE) {
      return {
        ok: false,
        kind: 'not-a-judge',
        message:
          'Você não está cadastrado como juiz deste torneio. Peça à organização para incluir você — as notas digitadas continuam aqui.',
      };
    }
    return {
      ok: false,
      kind: 'failed',
      message:
        getApiErrorMessage(err) ??
        'Não foi possível salvar. Confira a conexão e tente de novo — as notas digitadas continuam aqui.',
    };
  }
}
